/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ApiService } from '@/types/dashboard-types'

import type { MigrationFilters, MigrationNeeds, RunResult, RunTarget } from './types'

/** What is missing from the filters for this run, or undefined when it can go. */
export const missingFilter = (filters: MigrationFilters, needs: Pick<MigrationNeeds, 'asset'>): string | undefined => {
  if (!filters.companyId) return 'Choose a company'
  if (!filters.from || !filters.to) return 'Choose a date range'
  if (needs.asset && !filters.assetId) return 'Choose an asset'
  return undefined
}

/** Run the endpoint test or a SQL pair with the dashboard's filters. */
export const runMigration = async (
  api: ApiService,
  itemId: string,
  target: RunTarget,
  filters: MigrationFilters
): Promise<{ result?: RunResult; error?: string }> => {
  const id = encodeURIComponent(itemId)
  const path =
    target.kind === 'test'
      ? `/app/admin/migration/items/${id}/test`
      : `/app/admin/migration/items/${id}/sql/${encodeURIComponent(target.sqlId)}`
  const res = await api.post<RunResult, MigrationFilters>({
    path,
    body: {
      companyId: filters.companyId,
      assetId: filters.assetId,
      from: filters.from,
      to: filters.to,
      coordDigits: filters.coordDigits,
      numberDigits: filters.numberDigits,
    },
    origin: 'MigrationDashboard#run',
  })
  if (res.error || !res.data) return { error: res.message || 'The run failed' }
  return { result: res.data }
}

/** The compare page URL for a run, carrying the filters. */
export const comparePath = (itemId: string, target: RunTarget, search: string): string => {
  const params = new URLSearchParams(search)
  params.set('run', target.kind === 'test' ? 'test' : `sql:${target.sqlId}`)
  return `/admin/migration/${encodeURIComponent(itemId)}/compare?${params.toString()}`
}

export const parseRunTarget = (run: string | null): RunTarget =>
  run?.startsWith('sql:') ? { kind: 'sql', sqlId: run.slice(4) } : { kind: 'test' }
